import React, { useCallback } from 'react';
import { Card, Stack } from '@mantine/core';
import { useRouter } from 'next/router';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import dt from '~/testing';
import { getVisibleUserEntriesCount } from '~/domains/contest/domains/common/utils/pickControl';

import useGamecenterPath from '../../../gamecenter/domains/common/hooks/useGamecenterPath';

import useStyles from '~/domains/common/components/AlertCard/alertCard.styles';
import type { ContestInviteCardProps } from './types';
import ContestInviteCardInviterInfo from './ContestInviteCardInviterInfo';
import ContestInviteCardContestInfo from './ContestInviteCardContestInfo';
import ContestInviteCardActionButtons from './ContestInviteCardActionButtons';
import ContestInviteCardExpirationInfo from './ContestInviteCardExpirationInfo';

function ContestInviteCard({
  contest,
  invite,
  onAccept,
  onDecline,
  onClick,
  isLoading,
}: ContestInviteCardProps) {
  const { classes } = useStyles();
  const router = useRouter();

  const contestPath = useGamecenterPath({
    contestId: contest.id,
    contestType: contest?.contest_type,
    sport: contest?.league?.sport,
    contestStatus: contest?.status,
    userEntriesCount: getVisibleUserEntriesCount(contest),
  });

  const handleContestDetailOpen = useCallback(
    (e) => {
      onClick?.(e);
      const ignoreLinkContainer = e.currentTarget.querySelector('[data-ignore-link="true"]');
      if (
        e.target !== e.currentTarget &&
        (ignoreLinkContainer?.contains(e.target) || e.target?.tagName === 'A')
      ) {
        e.preventDefault();
      }
    },
    [onClick]
  );

  const handleAccept = useCallback(
    (inviteId: string, e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      onAccept?.(inviteId);
      router.push(contestPath);
    },
    [onAccept, router, contestPath]
  );

  const handleDecline = useCallback(
    (inviteId: string, e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      onDecline?.(inviteId);
    },
    [onDecline]
  );

  return (
    <Card
      className={classes.container}
      p="md"
      radius="lg"
      data-test-id={dt.contest.common.contestInviteCard}
      onClick={handleContestDetailOpen}
      style={{ cursor: 'pointer' }}
      component={UnstyledNextLink}
      href={contestPath}
    >
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={12}>
        <ContestInviteCardInviterInfo invite={invite} />
        <ContestInviteCardContestInfo contest={contest} />
        <ContestInviteCardActionButtons
          invite={invite}
          onAccept={handleAccept}
          onDecline={handleDecline}
          isLoading={isLoading}
        />
        <ContestInviteCardExpirationInfo invite={invite} />
      </Stack>
    </Card>
  );
}

export default ContestInviteCard;
